import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, Minus, Check, X, LogOut, AlertCircle, Search } from 'lucide-react';
import { useTenant } from '../../lib/tenant-context';
import { useTableManagement } from '../../lib/table-hooks';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { TableWithOrder, MenuItem, CartItemOption } from '../../lib/types';
import { formatCurrency, cn } from '../../lib/utils';
import { ProductDetailModal } from '../../components/restaurant/ProductDetailModal';
import { Button } from '../../components/ui/Button';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
interface WaiterCartItem {
  id: string;
  menuItem: MenuItem;
  quantity: number;
  options: CartItemOption[];
  notes: string;
}
const STORAGE_KEY_PREFIX = 'noiza_waiter_pos_';
function loadPersistedState(restaurantId: string | undefined) {
  if (!restaurantId || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${restaurantId}`);
    if (!raw) return null;
    return JSON.parse(raw) as {
      step: 1 | 2 | 3;
      selectedTable: TableWithOrder | null;
      cart: WaiterCartItem[];
    };
  } catch {
    return null;
  }
}
export function WaiterPOSPage() {
  const navigate = useNavigate();
  const {
    restaurant
  } = useTenant();
  const restaurantId = restaurant?.restaurant_id;
  const {
    tables,
    loading: tablesLoading
  } = useTableManagement(restaurantId);
  const {
    categories
  } = useMenuCategories(restaurantId);
  const {
    items
  } = useMenuItems(restaurantId);
  // Hydrate from localStorage so a refresh doesn't lose the in-progress order
  const persisted = useMemo(() => loadPersistedState(restaurantId), [restaurantId]);
  const [step, setStep] = useState<1 | 2 | 3>(persisted?.step ?? 1);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(persisted?.selectedTable ?? null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<WaiterCartItem[]>(persisted?.cart ?? []);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Persist whenever step / table / cart change
  useEffect(() => {
    if (!restaurantId || typeof window === 'undefined') return;
    const key = `${STORAGE_KEY_PREFIX}${restaurantId}`;
    if (cart.length === 0 && !selectedTable && step === 1) {
      // Nothing to persist — clear any stale state
      localStorage.removeItem(key);
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({
        step,
        selectedTable,
        cart
      }));
    } catch {
      // Storage full or unavailable — silently ignore
    }
  }, [restaurantId, step, selectedTable, cart]);
  // Filter active categories and items
  const activeCategories = useMemo(() => categories.filter(c => c.is_active).sort((a, b) => a.sort_order - b.sort_order), [categories]);
  const activeItems = useMemo(() => items.filter(i => i.is_active).sort((a, b) => a.sort_order - b.sort_order), [items]);
  // Set initial category
  useEffect(() => {
    if (activeCategories.length > 0 && !activeCategoryId) {
      setActiveCategoryId(activeCategories[0].id);
    }
  }, [activeCategories, activeCategoryId]);
  const cartTotal = cart.reduce((sum, item) => {
    const optionsTotal = item.options.reduce((optSum, opt) => optSum + (Number(opt.price_delta) || 0), 0);
    return sum + (Number(item.menuItem.price) + optionsTotal) * item.quantity;
  }, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const handleTableSelect = (table: TableWithOrder) => {
    setSelectedTable(table);
    setStep(2);
  };
  const handleAddToCart = (menuItem: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    setCart(prev => [...prev, {
      id: crypto.randomUUID(),
      menuItem,
      quantity,
      options,
      notes
    }]);
  };
  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? {
          ...item,
          quantity: newQty
        } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  const removeItem = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };
  const handleSubmitOrder = async () => {
    if (!restaurant || !selectedTable || cart.length === 0) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const orderIntentId = crypto.randomUUID();
      const payload = {
        restaurant_id: restaurantId,
        order_intent_id: orderIntentId,
        customer_name: `Mesa ${selectedTable.table_number}`,
        delivery_type: 'dine_in',
        table_id: selectedTable.id,
        source: 'waiter',
        subtotal: cartTotal,
        total: cartTotal,
        items: cart.map(item => ({
          menu_item_id: item.menuItem.id,
          item_name_snapshot: item.menuItem.name,
          unit_price_snapshot: item.menuItem.price,
          quantity: item.quantity,
          notes: item.notes,
          selected_options: item.options
        }))
      };
      console.log('[WAITER POS] Selected table:', selectedTable);
      console.log('[WAITER POS] Submitting payload:', payload);
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Error al crear la orden');
      }
      // Success! Reset and go back to table selection
      setCart([]);
      setSelectedTable(null);
      setStep(1);
      if (restaurantId && typeof window !== 'undefined') {
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}${restaurantId}`);
      }
    } catch (error: any) {
      console.error('[WAITER POS] Submit error:', error);
      setSubmitError(error.message || 'Error de conexión');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!restaurant) return null;
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-4799">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm" data-id="element-4800">
        <div className="flex items-center gap-3" data-id="element-4801">
          <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center text-white font-bold" data-id="element-4802">
            {restaurant.name.charAt(0)}
          </div>
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-4803">
            {restaurant.name}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm font-medium text-gray-500" data-id="element-4804">
          <span className={cn(step >= 1 ? 'text-brand-charcoal font-bold' : '')} data-id="element-4805">
            1 Mesa
          </span>
          <span data-id="element-4806">·</span>
          <span className={cn(step >= 2 ? 'text-brand-charcoal font-bold' : '')} data-id="element-4807">
            2 Productos
          </span>
          <span data-id="element-4808">·</span>
          <span className={cn(step >= 3 ? 'text-brand-charcoal font-bold' : '')} data-id="element-4809">
            3 Confirmar
          </span>
        </div>

        <button onClick={() => navigate(`/admin/${restaurant.slug}/operacion`)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" data-id="element-4810">
          <LogOut className="w-4 h-4" data-id="element-4811" />
          <span className="hidden sm:inline" data-id="element-4812">Salir</span>
        </button>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden" data-id="element-4813">
        {step === 1 && <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-4814">
            <div className="max-w-6xl mx-auto" data-id="element-4815">
              <h1 className="text-3xl font-black text-gray-900 mb-2" data-id="element-4816">
                Selecciona una mesa
              </h1>
              <p className="text-gray-500 mb-8" data-id="element-4817">
                Toca una mesa para tomar la orden
              </p>

              {tablesLoading ? <div className="flex items-center justify-center py-20" data-id="element-4818">
                  <div className="w-8 h-8 border-4 border-brand-charcoal border-t-transparent rounded-full animate-spin" data-id="element-4819"></div>
                </div> : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-id="element-4820">
                  {tables.map(table => {
              const isOccupied = table.status !== 'free';
              return <button key={table.id} onClick={() => handleTableSelect(table)} className={cn('flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all min-h-[140px]', isOccupied ? 'bg-amber-50 border-amber-200 hover:border-amber-400' : 'bg-white border-gray-100 hover:border-green-500 hover:shadow-md')} data-id="element-4821">
                        <span className="text-5xl font-black text-gray-900 mb-2" data-id="element-4822">
                          {table.table_number}
                        </span>
                        {table.table_name && <span className="text-xs font-medium text-gray-500 mb-3 text-center line-clamp-1" data-id="element-4823">
                            {table.table_name}
                          </span>}
                        <div className={cn('px-3 py-1 rounded-full text-xs font-bold', isOccupied ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800')} data-id="element-4824">
                          {isOccupied ? 'Ocupada' : 'Libre'}
                        </div>
                      </button>;
            })}
                </div>}
            </div>
          </div>}

        {step === 2 && selectedTable && <div className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-4825">
            {/* Sub-header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0" data-id="element-4826">
              <div className="flex items-center gap-3" data-id="element-4827">
                <button onClick={() => setStep(1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-4828">
                  <ArrowLeft className="w-6 h-6" data-id="element-4829" />
                </button>
                <div data-id="element-4830">
                  <h2 className="text-xl font-bold text-gray-900 leading-none" data-id="element-4831">
                    Mesa {selectedTable.table_number}
                  </h2>
                  {selectedTable.table_name && <span className="text-sm text-gray-500" data-id="element-4832">
                      {selectedTable.table_name}
                    </span>}
                </div>
              </div>

              <button onClick={() => setStep(3)} disabled={cart.length === 0} className={cn('flex items-center gap-3 px-4 py-2 rounded-xl font-bold transition-all', cart.length > 0 ? 'bg-brand-charcoal text-white hover:bg-gray-800 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-4833">
                <div className="flex items-center gap-2" data-id="element-4834">
                  <ShoppingBag className="w-5 h-5" data-id="element-4835" />
                  <span className="bg-white/20 px-2 py-0.5 rounded-md text-sm" data-id="element-4836">
                    {cartItemCount}
                  </span>
                </div>
                <span data-id="element-4837">{formatCurrency(cartTotal)}</span>
              </button>
            </div>

            {/* Search */}
            <div className="bg-white border-b border-gray-100 px-4 py-3 shrink-0" data-id="element-4838">
              <div className="relative max-w-2xl mx-auto" data-id="element-4839">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-4840" />
                <input type="text" inputMode="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar producto…" className="w-full h-12 pl-12 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-4841" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full" aria-label="Limpiar búsqueda" data-id="element-4842">
                    <X className="w-4 h-4" data-id="element-4843" />
                  </button>}
              </div>
            </div>

            {/* Categories — hide while searching */}
            {!searchQuery.trim() && <div className="bg-white border-b border-gray-100 overflow-x-auto shrink-0 hide-scrollbar" data-id="element-4844">
                <div className="flex p-2 gap-2 min-w-max" data-id="element-4845">
                  {activeCategories.map(cat => <button key={cat.id} onClick={() => setActiveCategoryId(cat.id)} className={cn('px-6 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap', activeCategoryId === cat.id ? 'bg-brand-charcoal text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100')} data-id="element-4846">
                      {cat.name}
                    </button>)}
                </div>
              </div>}

            {/* Items Grid */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-4847">
              {(() => {
            const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const q = normalize(searchQuery.trim());
            const filteredItems = q ? activeItems.filter(item => {
              const haystack = normalize(`${item.name} ${item.description || ''}`);
              return haystack.includes(q);
            }) : activeItems.filter(item => item.category_id === activeCategoryId);
            if (filteredItems.length === 0) {
              return <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-4848">
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-4849">
                        <Search className="w-7 h-7 text-gray-400" data-id="element-4850" />
                      </div>
                      <p className="text-lg font-bold text-gray-900 mb-1" data-id="element-4851">
                        {q ? 'Sin resultados' : 'No hay productos'}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-4852">
                        {q ? `No encontramos productos para "${searchQuery}"` : 'No hay productos en esta categoría'}
                      </p>
                    </div>;
            }
            return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-4853">
                    {filteredItems.map(item => {
                const cartItem = cart.find(c => c.menuItem.id === item.id);
                return <div key={item.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col h-full" data-id="element-4854">
                          <div className="flex-1" data-id="element-4855">
                            <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1 line-clamp-2" data-id="element-4856">
                              {item.name}
                            </h3>
                            {item.description && <p className="text-sm text-gray-500 line-clamp-2 mb-3" data-id="element-4857">
                                {item.description}
                              </p>}
                          </div>
                          <div className="flex items-end justify-between mt-4" data-id="element-4858">
                            <span className="font-bold text-xl text-green-600" data-id="element-4859">
                              {formatCurrency(Number(item.price))}
                            </span>

                            {cartItem && !item.is_customizable ? <div className="flex items-center bg-gray-100 rounded-full p-1" data-id="element-4860">
                                <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, -1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-4861">
                                  <Minus className="w-4 h-4" data-id="element-4862" />
                                </button>
                                <span className="w-8 text-center font-bold text-gray-900" data-id="element-4863">
                                  {cartItem.quantity}
                                </span>
                                <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, 1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-4864">
                                  <Plus className="w-4 h-4" data-id="element-4865" />
                                </button>
                              </div> : <button onClick={() => setSelectedProduct(item)} className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors" data-id="element-4866">
                                <Plus className="w-6 h-6" data-id="element-4867" />
                              </button>}
                          </div>
                        </div>;
              })}
                  </div>;
          })()}
            </div>
          </div>}

        {step === 3 && selectedTable && <div className="flex-1 flex flex-col h-full overflow-hidden bg-white" data-id="element-4868">
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shrink-0" data-id="element-4869">
              <button onClick={() => setStep(2)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-4870">
                <ArrowLeft className="w-6 h-6" data-id="element-4871" />
              </button>
              <h2 className="text-xl font-bold text-gray-900" data-id="element-4872">
                Confirmar Orden - Mesa {selectedTable.table_number}
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-4873">
              <div className="max-w-3xl mx-auto" data-id="element-4874">
                {submitError && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-800" data-id="element-4875">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-4876" />
                    <p className="text-sm font-medium" data-id="element-4877">{submitError}</p>
                  </div>}

                <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden mb-6" data-id="element-4878">
                  <div className="divide-y divide-gray-100" data-id="element-4879">
                    {cart.map(item => {
                  const optionsTotal = item.options.reduce((sum, opt) => sum + (Number(opt.price_delta) || 0), 0);
                  const itemTotal = (Number(item.menuItem.price) + optionsTotal) * item.quantity;
                  return <div key={item.id} className="p-4 flex gap-4" data-id="element-4880">
                          <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 h-fit shrink-0" data-id="element-4881">
                            <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-4882">
                              <Minus className="w-4 h-4" data-id="element-4883" />
                            </button>
                            <span className="w-8 text-center font-bold text-gray-900" data-id="element-4884">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-4885">
                              <Plus className="w-4 h-4" data-id="element-4886" />
                            </button>
                          </div>

                          <div className="flex-1 min-w-0" data-id="element-4887">
                            <div className="flex justify-between items-start gap-4" data-id="element-4888">
                              <h4 className="font-bold text-gray-900 text-lg" data-id="element-4889">
                                {item.menuItem.name}
                              </h4>
                              <span className="font-bold text-gray-900 whitespace-nowrap" data-id="element-4890">
                                {formatCurrency(itemTotal)}
                              </span>
                            </div>

                            {item.options.length > 0 && <ul className="mt-1 space-y-0.5" data-id="element-4891">
                                {item.options.map((opt, idx) => <li key={idx} className="text-sm text-gray-500 flex justify-between" data-id="element-4892">
                                    <span data-id="element-4893">• {opt.option_name}</span>
                                    {Number(opt.price_delta) > 0 && <span data-id="element-4894">
                                        +
                                        {formatCurrency(Number(opt.price_delta))}
                                      </span>}
                                  </li>)}
                              </ul>}

                            {item.notes && <p className="mt-2 text-sm text-amber-700 bg-amber-50 p-2 rounded-lg" data-id="element-4895">
                                Nota: {item.notes}
                              </p>}
                          </div>

                          <button onClick={() => removeItem(item.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg h-fit transition-colors" data-id="element-4896">
                            <X className="w-5 h-5" data-id="element-4897" />
                          </button>
                        </div>;
                })}
                  </div>

                  <div className="p-4 bg-white border-t border-gray-100 flex justify-between items-center" data-id="element-4898">
                    <span className="text-lg font-medium text-gray-500" data-id="element-4899">
                      Total a cobrar
                    </span>
                    <span className="text-3xl font-black text-gray-900" data-id="element-4900">
                      {formatCurrency(cartTotal)}
                    </span>
                  </div>
                </div>

                <Button onClick={handleSubmitOrder} disabled={isSubmitting || cart.length === 0} className="w-full h-16 text-xl font-bold bg-green-500 hover:bg-green-600 text-white rounded-2xl" data-id="element-4901">
                  {isSubmitting ? <div className="flex items-center gap-2" data-id="element-4902">
                      <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" data-id="element-4903" />
                      <span data-id="element-4904">Enviando a cocina...</span>
                    </div> : <div className="flex items-center gap-2" data-id="element-4905">
                      <Check className="w-6 h-6" data-id="element-4906" />
                      <span data-id="element-4907">Confirmar y Enviar Orden</span>
                    </div>}
                </Button>
              </div>
            </div>
          </div>}
      </main>

      {/* Product Detail Modal */}
      {selectedProduct && <ProductDetailModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} item={selectedProduct} onAdd={handleAddToCart} data-id="element-4908" />}
    </div>;
}